import { Job, JobStatus } from '@/shared/types';
import type { CreateInvoiceData } from '@/shared/types/invoice';
import { supabaseService } from '@/shared/services/supabaseService';
import { authService } from '@/shared/services/realData/auth';
import { quoteService } from '@/shared/services/realData/quotes';
import { generateShareToken } from '@/shared/utils/shareToken';

/**
 * Job operations
 * Handles all job-related database operations
 * Note: Jobs are derived from quotes in this system
 */
export const jobService = {
  /**
   * Get all jobs (derived from quotes for backward compatibility)
   */
  async getJobs(): Promise<Job[]> {
    const quotes = await quoteService.getQuotes();
    const { clientService } = await import('@/shared/services/realData/clients');
    const clients = await clientService.getClients();

    return quotes.map(quote => ({
      id: quote.id,
      userId: quote.userId,
      clientId: quote.clientId || '',
      title: quote.title,
      services: [],
      status: this.mapQuoteStatusToJobStatus(quote.status),
      totalAmount: quote.totalAmount,
      amountPaid: 0,
      createdAt: quote.createdAt,
      dueDate: quote.validUntil || ''
    } as Job));
  },

  /**
   * Add a new job and automatically create an invoice
   */
  async addJob(job: Job): Promise<Job> {
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) throw new Error('User not authenticated');

    // Insert the job
    const { error: jobError } = await supabaseService.client
      .from('jobs')
      .insert({
        id: job.id,
        user_id: job.userId,
        client_id: job.clientId,
        title: job.title,
        description: job.description,
        status: job.status,
        total_amount: job.totalAmount,
        amount_paid: job.amountPaid,
        due_date: job.dueDate,
        payment_method_id: job.paymentMethodId,
        public_token: job.publicToken,
        created_at: job.createdAt
      });

    if (jobError) throw jobError;

    // Automatically create an invoice for this job
    await this.createAutoInvoice(job, supabaseUserId);

    return job;
  },

  /**
   * Automatically create an invoice for a job
   */
  async createAutoInvoice(job: Job, supabaseUserId: string): Promise<void> {
    // Generate invoice number
    const invoiceNumber = await this.generateInvoiceNumber();

    // Get currency from profile
    const { realDataService } = await import('@/shared/services/realDataService');
    const profile = await realDataService.fetchProfile();
    const currency = profile?.currency || 'USD';

    // Calculate totals
    const subtotal = job.totalAmount || 0;
    const taxRate = 0;
    const taxAmount = 0;
    const totalAmount = subtotal + taxAmount;

    // Use invoiceDueDate if provided, otherwise fall back to job due date + 30 days, or today + 30 days
    const invoiceDueDate = job.invoiceDueDate || job.dueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    // Create invoice
    const { data: newInvoice, error: invoiceError } = await supabaseService.client
      .from('invoices')
      .insert({
        user_id: supabaseUserId,
        client_id: job.clientId,
        job_id: job.id,
        invoice_number: invoiceNumber,
        title: `Invoice for ${job.title}`,
        description: job.description || null,
        status: 'sent',
        currency: currency,
        subtotal,
        tax_rate: taxRate,
        tax_amount: taxAmount,
        total_amount: totalAmount,
        paid_amount: 0,
        issue_date: new Date().toISOString().split('T')[0],
        due_date: invoiceDueDate,
        notes: `Auto-generated invoice for job: ${job.title}`,
        terms: null,
        public_token: generateShareToken(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (invoiceError) {
      console.error('Failed to create auto-invoice for job:', invoiceError);
      return;
    }

    // Add job services as invoice items if they exist
    if (job.services && job.services.length > 0) {
      const itemsToInsert = job.services.map((service, index) => ({
        invoice_id: newInvoice.id,
        name: service.name,
        description: service.description || null,
        quantity: 1,
        unit_price: service.rate || 0,
        total_price: service.rate || 0,
        order_index: index
      }));

      await supabaseService.client
        .from('invoice_items')
        .insert(itemsToInsert);
    }

    console.log(`✅ Auto-created invoice ${invoiceNumber} for job: ${job.title} (due: ${invoiceDueDate})`);
  },

  /**
   * Generate a unique invoice number
   */
  async generateInvoiceNumber(): Promise<string> {
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) throw new Error('User not authenticated');

    const { data, error } = await supabaseService.client
      .from('invoices')
      .select('invoice_number')
      .eq('user_id', supabaseUserId)
      .order('invoice_number', { ascending: false })
      .limit(1);

    if (error) throw error;

    let nextNum = 1;
    if (data && data.length > 0) {
      const lastInvoice = data[0];
      if (!lastInvoice) {
        return `INV-0001`;
      }
      const lastNumber = lastInvoice.invoice_number;
      const match = lastNumber.match(/(\d+)$/);
      if (match) {
        nextNum = parseInt(match[1]) + 1;
      }
    }

    return `INV-${String(nextNum).padStart(4, '0')}`;
  },

  /**
   * Helper function to map quote status to job status
   */
  mapQuoteStatusToJobStatus(status: string): JobStatus {
    switch (status) {
      case 'accepted':
        return JobStatus.COMPLETED;
      case 'rejected':
        return JobStatus.CANCELLED;
      case 'sent':
      case 'draft':
        return JobStatus.IN_PROGRESS;
      default:
        return JobStatus.PENDING;
    }
  },

  /**
   * Helper function to map invoice status to job status
   */
  mapInvoiceStatusToJobStatus(invoiceStatus: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled'): JobStatus {
    switch (invoiceStatus) {
      case 'paid':
        return JobStatus.COMPLETED;
      case 'cancelled':
        return JobStatus.CANCELLED;
      case 'sent':
      case 'overdue':
        return JobStatus.IN_PROGRESS;
      case 'draft':
      default:
        return JobStatus.PENDING;
    }
  }
};
